import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  ClipboardType,
  FileText,
  Globe,
  Upload,
  X,
} from "lucide-react";
import {
  MENSAJE_LINK_VIDEO,
  MENSAJE_RECHAZO,
  interpretarCanalCreador,
  datosCreadorDeVideo,
  interpretarLinkVideo,
  interpretarUrlFuente,
  mensajeCanalCreador,
  type DatosCreador,
  type PlataformaReferencia,
  type ResultadoAgregar,
} from "../datos/canalesReferencia";
import {
  MAX_TEXTO,
  MENSAJE_PAGINA_WEB,
  MENSAJE_TEXTO_PEGADO,
  anotarArchivo,
  contarTexto,
  formatearTamano,
  interpretarPaginaWeb,
  materialTexto,
  materialWeb,
  mensajeMaterialesAnotados,
  titularTexto,
  validarTextoPegado,
  type MaterialArchivo,
  type MaterialConocimiento,
  type ResumenAgregado,
} from "../datos/materialesConocimiento";
import { useSoltarArchivos } from "../datos/useSoltarArchivos";
import CachedImg from "../../../components/CachedImg";
import { MarcoLogo, NOMBRE_MARCA, type MarcaLogo } from "./LogosMarcas";
import { CLASE_FOCO, CLASE_ROTULO } from "./tarjetasFuente";

/**
 * "Añadir fuente": el modal que junta las cinco formas de traer material a la
 * base de conocimiento.
 *
 * NAVEGACIÓN ADENTRO, NO MODALES APILADOS. Es una sola ventana con una máquina
 * de estados de seis pantallas (`Pantalla`): la inicial con los cinco atajos y
 * una por atajo, con vuelta atrás. Abrir un segundo modal encima del primero
 * duplicaría el diálogo, el trap de foco y el `aria-modal` —dos regiones que se
 * declaran las dos como "lo único que existe"— y dejaría al usuario de teclado
 * sin forma clara de volver.
 *
 * PORTAL A `document.body`, obligatorio. En desktop el panel de contenido de la
 * app es `position: fixed`, así que un overlay montado dentro de una página
 * queda por DEBAJO de la sidebar y del encabezado: se ve el fondo oscurecido
 * con la navegación encima, clickeable. (No confundir con el panel de chat, que
 * NO va portaleado porque empuja el contenido en vez de taparlo.)
 *
 * ACCESIBILIDAD, sin excusas: `role="dialog"` + `aria-modal`, foco atrapado
 * adentro mientras está abierto, Escape cierra, el foco vuelve al botón que lo
 * abrió, y la vuelta atrás es un `<button>` de verdad (se alcanza tabulando y
 * se dispara con Enter y con Espacio). Cada cambio de pantalla mueve el foco al
 * título, que es lo que hace que un lector de pantalla ANUNCIE la pantalla
 * nueva: sin eso, "Vincular canal de creador" abre una vista que para alguien
 * que no ve la pantalla no pasó nada.
 *
 * QUÉ PASA CON LO QUE SE CARGA — la regla que manda acá. Esto sigue siendo la
 * pantalla: el procesamiento se construye después. Cada sub-pantalla dice, en
 * su propio texto y antes de que el usuario apriete nada, dónde queda lo que
 * carga. Y el caso que no se puede maquillar: LOS ARCHIVOS NO SE SUBEN. No hay
 * backend y en localStorage no entra un archivo, así que del archivo elegido se
 * anotan nombre, tamaño y tipo — y se dice tres veces (en el atajo, en la
 * confirmación previa a anotar y en la tarjeta de la lista). Ver
 * ADR-2026-08-17-estudio-marketing-modal-anadir-fuente.
 */

/** Lo que la pantalla de atrás tiene que contar después de que el modal cierra.
 *  El modal no lo muestra él mismo porque el resultado se ve en la lista, que
 *  está detrás: el aviso tiene que quedar al lado de lo que apareció. */
export type AvisoAnadido =
  | { clase: "canal"; nombre: string }
  | { clase: "material"; texto: string };

/**
 * Las seis vistas del modal. Se exporta porque quien lo abre puede pedirle que
 * ARRANQUE en una: la Puesta en marcha entra directo a `video` desde su tarjeta
 * de "Canales de creadores" (`pantallaInicial`).
 */
export type Pantalla = "inicio" | "video" | "creador" | "web" | "texto" | "archivos";

const TITULO: Record<Pantalla, string> = {
  inicio: "Añadir fuente",
  video: "Extraer conocimiento de video",
  creador: "Vincular canal de creador",
  web: "Página web",
  texto: "Texto copiado",
  archivos: "Antes de anotarlos",
};

const CLASE_BOTON_PRIMARIO = `rounded-xl bg-brand-primary py-2.5 px-5 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 ${CLASE_FOCO}`;
const CLASE_BOTON_SECUNDARIO = `rounded-xl border border-[#02062B]/[0.12] bg-white py-2.5 px-5 text-[13px] font-medium text-brand-black transition-colors hover:bg-[#F0F2F7] ${CLASE_FOCO}`;
/** Sólo para "Descartar": la acción que pierde lo escrito se ve distinta de la
 *  que sigue el camino normal. */
const CLASE_BOTON_DESTRUCTIVO = `rounded-xl bg-[#C33B36] py-2.5 px-5 text-[13px] font-medium text-white transition-opacity hover:opacity-90 ${CLASE_FOCO}`;
const CLASE_ICONO_HEADER = `inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#02062B]/[0.55] transition-colors hover:bg-[#F0F2F7] hover:text-brand-black ${CLASE_FOCO}`;
const CLASE_CAMPO =
  "mt-1.5 w-full rounded-xl border bg-white py-2.5 px-4 text-[13px] text-brand-black outline-none transition-colors placeholder:text-[#02062B]/[0.35] focus:border-[#1644EB]";

/** Los elementos que pueden recibir el foco dentro del diálogo. El `:not` del
 *  `tabindex="-1"` deja afuera el título (que se enfoca por código y no tiene
 *  que ser una parada de tabulación). */
const SELECTOR_FOCO =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/* ── Piezas compartidas por las sub-pantallas ───────────────────────────── */

/**
 * La nota que dice DÓNDE QUEDA lo que se está por cargar. Va en todas las
 * sub-pantallas, arriba del campo: enterarse después de haber cargado no sirve
 * de nada. `alerta` la pinta ámbar — se reserva para los archivos, que es el
 * único caso donde lo que el usuario cree que pasó y lo que pasa de verdad son
 * cosas distintas.
 */
const NotaDestino = ({ alerta = false, children }: { alerta?: boolean; children: ReactNode }) => (
  <p
    className={`flex items-start gap-2 rounded-xl py-3 px-4 text-[12.5px] leading-relaxed ${
      alerta ? "bg-[#F7EBD5] text-[#8A5A08]" : "bg-[#F0F2F7] text-[#02062B]/[0.65]"
    }`}
  >
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
    <span>{children}</span>
  </p>
);

const MensajeError = ({ id, children }: { id: string; children: ReactNode }) => (
  <p id={id} role="alert" className="mt-2 text-[12.5px] leading-relaxed text-[#C33B36]">
    {children}
  </p>
);

/** Un atajo de la pantalla inicial. La fila entera es el botón. */
const Atajo = ({
  icono,
  logos,
  titulo,
  detalle,
  onClick,
}: {
  icono?: ReactNode;
  logos?: MarcaLogo[];
  titulo: string;
  detalle: string;
  onClick: () => void;
}) => (
  <li className="flex">
    <button
      type="button"
      onClick={onClick}
      className={`group flex h-full w-full items-center gap-3 rounded-xl border border-[#02062B]/[0.08] bg-white py-3 px-4 text-left transition-colors hover:border-[#1644EB] hover:bg-[#F7F9FC] ${CLASE_FOCO}`}
    >
      {logos ? (
        // Los tres logos, apenas montados uno sobre otro: dicen de qué
        // plataformas se puede traer sin escribirlo en el título.
        <span className="flex shrink-0 -space-x-4" aria-hidden="true">
          {logos.map((marca) => (
            <MarcoLogo key={marca} marca={marca} tamano="sm" />
          ))}
        </span>
      ) : (
        <span
          aria-hidden="true"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#02062B]/[0.08] bg-white text-[#1644EB]"
        >
          {icono}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold text-brand-black">{titulo}</span>
        <span className="block text-[12px] leading-relaxed text-[#02062B]/[0.55]">{detalle}</span>
      </span>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-[#02062B]/[0.35] transition-colors group-hover:text-[#1644EB]"
        aria-hidden="true"
      />
    </button>
  </li>
);

/**
 * La tarjeta del creador, debajo del campo del video.
 *
 * LO QUE MUESTRA ES SÓLO LO QUE SE PUEDE DERIVAR DEL LINK. No hay backend que
 * salga a preguntarle a YouTube quién hizo un video, así que:
 *  · la miniatura aparece únicamente en YouTube (su dirección se arma con el id);
 *  · el nombre aparece únicamente cuando el link trae el `@handle` (TikTok
 *    siempre, YouTube sólo en los compartidos desde el perfil);
 *  · en Instagram no hay ni una cosa ni la otra.
 * Lo que falta va como HUECO ROTULADO, nunca como un nombre inventado: esta
 * pantalla existe para cargar referentes, y un creador falso acá es peor que
 * un espacio vacío. Ver `datosCreadorDeVideo` en `datos/canalesReferencia.ts`.
 */
const TarjetaCreador = ({
  datos,
  onAnadirCanal,
}: {
  datos: DatosCreador;
  onAnadirCanal: (urlCanal: string) => void;
}) => (
  <div className="mt-4 flex items-center gap-3 rounded-xl border border-[#02062B]/[0.08] bg-[#FAFBFD] p-3">
    {datos.miniatura ? (
      // `CachedImg` y no `<img>`: es la regla del repo para cualquier imagen
      // remota (evita el ícono roto y el flash del nombre de archivo).
      <CachedImg
        src={datos.miniatura}
        alt=""
        className="h-12 w-20 shrink-0 rounded-lg object-cover"
      />
    ) : (
      <span
        aria-hidden="true"
        className="flex h-12 w-20 shrink-0 items-center justify-center rounded-lg border border-dashed border-[#02062B]/[0.16] text-[10px] text-[#02062B]/[0.35]"
      >
        sin vista
      </span>
    )}
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] font-semibold text-brand-black">
        {datos.usuario ? `@${datos.usuario}` : "Creador por identificar"}
      </span>
      <span className="block truncate text-[12px] text-[#02062B]/[0.55]">
        {datos.usuario
          ? `Creador en ${NOMBRE_MARCA[datos.plataforma]}`
          : `${NOMBRE_MARCA[datos.plataforma]} · el link no dice de quién es`}
      </span>
    </span>
    {datos.urlCanal ? (
      <button
        type="button"
        onClick={() => onAnadirCanal(datos.urlCanal as string)}
        className={`shrink-0 rounded-full border border-[#1644EB] py-1.5 px-3 text-[12px] font-medium text-[#1644EB] transition-colors hover:bg-[#1644EB]/[0.06] ${CLASE_FOCO}`}
      >
        Añadir canal completo
      </button>
    ) : (
      // Sin `urlCanal` no se puede sumar el canal sin salir a preguntar. El
      // botón NO se dibuja apagado y mudo: se dice por qué.
      <span className="shrink-0 text-right text-[11px] leading-tight text-[#02062B]/[0.45]">
        El canal se
        <br />
        resuelve después
      </span>
    )}
  </div>
);

/**
 * La zona para soltar archivos, arriba de los atajos.
 *
 * El manejo del arrastre (el `preventDefault` que evita que la pestaña navegue
 * al archivo, el contador que apaga el resaltado) vive en `useSoltarArchivos`:
 * lo comparte con las franjas del paso de sistema de diseño, que se ven
 * distinto pero se rompen igual.
 */
const ZonaSoltar = ({ onArchivos }: { onArchivos: (archivos: File[]) => void }) => {
  const { encima, props } = useSoltarArchivos(onArchivos);

  return (
    <div
      {...props}
      className={`mt-4 flex flex-col items-center justify-center rounded-xl border border-dashed py-6 px-4 text-center transition-colors ${
        encima ? "border-[#1644EB] bg-[#1644EB]/[0.04]" : "border-[#02062B]/[0.16] bg-[#FAFBFD]"
      }`}
    >
      <Upload
        className={`h-5 w-5 ${encima ? "text-[#1644EB]" : "text-[#02062B]/[0.35]"}`}
        aria-hidden="true"
      />
      <p className="mt-2 text-[13px] font-medium text-brand-black">Soltá archivos acá</p>
      {/* El aviso va en la zona, no sólo en la confirmación: es el único lugar
          que alguien mira antes de arrastrar. */}
      <p className="mt-0.5 text-[12px] text-[#02062B]/[0.5]">
        O elegilos desde tu computadora con “Subir archivos”.
      </p>
    </div>
  );
};

/** Las tres plataformas del atajo de creador, en el orden en que se piden. */
const PLATAFORMAS_CREADOR: { plataforma: PlataformaReferencia; nombre: string }[] = [
  { plataforma: "youtube", nombre: "YouTube" },
  { plataforma: "instagram", nombre: "Instagram" },
  { plataforma: "tiktok", nombre: "TikTok" },
];

/**
 * CADA PLATAFORMA PIDE UNA COSA DISTINTA, y el campo lo dice: la etiqueta, el
 * ejemplo y la ayuda cambian con la plataforma elegida, igual que la validación
 * (`interpretarCanalCreador`). YouTube necesita el LINK —conviven `@handle`,
 * `/channel/UC…`, `/c/…` y `/user/…`, y no hay forma de adivinar cuál es a
 * partir de un nombre—; Instagram y TikTok piden el USUARIO, con reglas
 * distintas entre sí (largo mínimo y máximo, y el punto adelante).
 */
const CAMPO_CREADOR: Record<
  PlataformaReferencia,
  { etiqueta: string; placeholder: string; ayuda: string }
> = {
  youtube: {
    etiqueta: "Link del canal",
    placeholder: "https://www.youtube.com/@nombre",
    ayuda:
      "Copiá la dirección desde el navegador. Sirven /@nombre, /channel/…, /c/… y /user/…. El nombre solo no alcanza.",
  },
  instagram: {
    etiqueta: "Usuario de Instagram",
    placeholder: "@usuario",
    ayuda:
      "Con arroba o sin arroba, hasta 30 caracteres (letras, números, puntos y guiones bajos). También podés pegar el link del perfil.",
  },
  tiktok: {
    etiqueta: "Usuario de TikTok",
    placeholder: "@usuario",
    ayuda:
      "Con arroba o sin arroba, entre 2 y 24 caracteres (letras, números, puntos y guiones bajos). También podés pegar el link del perfil.",
  },
};

interface PropsModal {
  onCerrar: () => void;
  /** El botón que abrió el modal: ahí vuelve el foco al cerrar. Se recibe por
   *  ref y no se confía en `document.activeElement` porque Safari NO enfoca un
   *  `<button>` al clickearlo: el foco estaría en el `<body>` y volvería ahí,
   *  o sea al principio del documento. */
  volverElFocoA: RefObject<HTMLElement | null>;
  /** Agrega un canal o un video a la lista de canales de referencia (mismo
   *  camino, mismo dedup y mismos rechazos que el formulario de la pantalla). */
  agregarCanal: (url: string, nombre?: string) => ResultadoAgregar;
  agregarMateriales: (nuevos: MaterialConocimiento[]) => ResumenAgregado;
  onGuardado: (aviso: AvisoAnadido) => void;
  /**
   * En qué vista abre. Por defecto la inicial (los cinco atajos), que es como
   * lo abre "Añadir fuente" en Fuentes de datos.
   *
   * Existe para quien ya sabe qué va a cargar: la tarjeta "Canales de
   * creadores" de la Puesta en marcha abre derecho en `video`, porque el botón
   * que se apretó YA dice qué se va a pegar y obligar a elegir de nuevo el
   * mismo atajo es un paso de más. NO es una segunda forma de abrir el modal:
   * es el mismo componente, con su navegación interna intacta — la flecha de
   * volver lleva a la pantalla inicial, así que desde ahí se llega igual a los
   * otros cuatro atajos.
   */
  pantallaInicial?: Pantalla;
}

export const ModalAnadirFuente = ({
  onCerrar,
  volverElFocoA,
  agregarCanal,
  agregarMateriales,
  onGuardado,
  pantallaInicial = "inicio",
}: PropsModal) => {
  // Sólo el valor INICIAL: si `pantallaInicial` cambiara con el modal abierto,
  // moverle la vista al usuario debajo del cursor sería peor que ignorarlo. La
  // navegación de acá en adelante es la de siempre (`irA`).
  const [pantalla, setPantalla] = useState<Pantalla>(pantallaInicial);
  const panelRef = useRef<HTMLDivElement>(null);
  const tituloRef = useRef<HTMLHeadingElement>(null);
  const inputArchivosRef = useRef<HTMLInputElement>(null);
  const idBase = useId();

  /* ── Estado de cada sub-pantalla ──
     Uno por atajo, y no un campo compartido: volver atrás y entrar a otro
     atajo no puede arrastrar el texto del anterior (un link de video en el
     campo "usuario de TikTok"), y volver al MISMO atajo tiene que encontrar lo
     que se estaba escribiendo. */
  const [linkVideo, setLinkVideo] = useState("");
  const [plataformaCreador, setPlataformaCreador] = useState<PlataformaReferencia>("youtube");
  const [valorCreador, setValorCreador] = useState("");
  const [linkWeb, setLinkWeb] = useState("");
  const [tituloTexto, setTituloTexto] = useState("");
  const [texto, setTexto] = useState("");
  const [archivosElegidos, setArchivosElegidos] = useState<MaterialArchivo[]>([]);
  const [archivosRechazados, setArchivosRechazados] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** Se pidió cerrar con algo escrito: la pregunta ocupa el cuerpo del modal. */
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);
  const seguirEditandoRef = useRef<HTMLButtonElement>(null);

  /**
   * CERRAR NO PUEDE TIRAR LO ESCRITO SIN PREGUNTAR. El fondo ya usaba
   * `mouseDown` en vez de `click` para que arrastrar una selección y soltar
   * afuera no cerrara el modal, pero eso tapaba UN camino: un clic limpio en el
   * fondo, la ✕ o un Escape por reflejo —capturado en `document`, así que llega
   * aunque el foco esté en el `<textarea>`— terminaban igual, y el estado vive
   * en este componente, que el padre DESMONTA al cerrar. Se pierde hasta
   * `MAX_TEXTO` (20.000) caracteres de un brief pegado, sin deshacer.
   *
   * Guardar no pasa por acá: `guardarCanal` y `guardarMateriales` llaman a
   * `onCerrar` directo, porque ahí no se pierde nada.
   */
  const hayBorrador =
    linkVideo.trim() !== "" ||
    valorCreador.trim() !== "" ||
    linkWeb.trim() !== "" ||
    tituloTexto.trim() !== "" ||
    texto.trim() !== "" ||
    archivosElegidos.length > 0;

  const pedirCierre = () => {
    if (hayBorrador) {
      setConfirmandoCierre(true);
      return;
    }
    onCerrar();
  };

  /** Toda la navegación pasa por acá: limpiar el error al cambiar de pantalla
   *  es obligatorio (un error de otra pantalla no significa nada en ésta). */
  const irA = (siguiente: Pantalla) => {
    setError(null);
    setPantalla(siguiente);
  };

  // Foco inicial y en cada cambio de pantalla: al TÍTULO. Es lo que hace que un
  // lector de pantalla lea "Vincular canal de creador" cuando la vista cambia
  // sin que la página navegue. Enfocar el primer campo sería más cómodo con
  // mouse y dejaría la pantalla nueva sin anunciar.
  useEffect(() => {
    tituloRef.current?.focus({ preventScroll: true });
  }, [pantalla]);

  // La pregunta de descarte reemplaza el cuerpo del modal: el foco tiene que ir
  // a "Seguir editando" (la salida segura), y volver al título al cancelarla.
  useEffect(() => {
    if (confirmandoCierre) seguirEditandoRef.current?.focus({ preventScroll: true });
    else tituloRef.current?.focus({ preventScroll: true });
  }, [confirmandoCierre]);

  // El foco vuelve al botón que abrió el modal. Va en un efecto de montaje
  // (limpieza al desmontar) para que valga por cualquier camino de cierre:
  // Escape, la ✕, el fondo o un guardado exitoso.
  useEffect(() => {
    const previo = document.activeElement as HTMLElement | null;
    return () => {
      const destino = volverElFocoA.current ?? previo;
      destino?.focus?.();
    };
  }, [volverElFocoA]);

  /**
   * Lo que hace Escape, siempre el de ESTE render. El listener se registra una
   * sola vez (depende de `onCerrar`, que es estable) y lee la versión fresca
   * por ref: sin esto habría que re-registrarlo en cada tecleo del textarea
   * para que `hayBorrador` no quedara viejo.
   */
  const alEscape = () => {
    // Con la pregunta abierta, Escape es "seguir editando": la tecla que se
    // apretó por reflejo no puede ser la que confirma el descarte.
    if (confirmandoCierre) {
      setConfirmandoCierre(false);
      return;
    }
    pedirCierre();
  };
  const alEscapeRef = useRef(alEscape);
  useEffect(() => {
    alEscapeRef.current = alEscape;
  });

  // Escape cierra + trap de Tab. El trap se calcula en cada tecla y no una vez
  // al abrir: las pantallas tienen distintos controles y la lista cambia.
  useEffect(() => {
    const alTeclado = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") {
        evento.preventDefault();
        alEscapeRef.current();
        return;
      }
      if (evento.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const enfocables = Array.from(panel.querySelectorAll<HTMLElement>(SELECTOR_FOCO)).filter(
        // El `<input type="file">` está oculto con `display:none` y no es
        // tabulable: contarlo correría el primero y el último de la rueda a un
        // elemento al que el foco no puede llegar, y el trap se saldría.
        (elemento) => elemento.offsetParent !== null || elemento === document.activeElement,
      );
      if (enfocables.length === 0) return;
      const primero = enfocables[0];
      const ultimo = enfocables[enfocables.length - 1];
      const actual = document.activeElement as HTMLElement | null;
      const indice = actual ? enfocables.indexOf(actual) : -1;
      if (indice === -1) {
        // El foco está en el título (`tabindex="-1"`, recién cambió de
        // pantalla). Con Tab el orden natural ya cae adentro; con Shift+Tab se
        // iría a la barra del navegador, así que se lo manda al final.
        if (evento.shiftKey) {
          evento.preventDefault();
          ultimo.focus();
        }
        return;
      }
      if (evento.shiftKey && actual === primero) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && actual === ultimo) {
        evento.preventDefault();
        primero.focus();
      }
    };
    document.addEventListener("keydown", alTeclado);
    return () => document.removeEventListener("keydown", alTeclado);
  }, [onCerrar]);

  /* ── Guardar ── */

  const guardarCanal = (url: string, nombre: string) => {
    const resultado = agregarCanal(url, nombre);
    if (!resultado.ok) {
      setError(MENSAJE_RECHAZO[resultado.motivo]);
      return;
    }
    onGuardado({ clase: "canal", nombre: resultado.fuente.nombre });
    onCerrar();
  };

  const guardarMateriales = (nuevos: MaterialConocimiento[], yaEstaba: string) => {
    const resumen = agregarMateriales(nuevos);
    if (resumen.agregados === 0) {
      setError(yaEstaba);
      return;
    }
    onGuardado({ clase: "material", texto: mensajeMaterialesAnotados(resumen) });
    onCerrar();
  };

  /* La tarjeta sale del link tipeado, DERIVADA y no en estado: un `useState`
     paralelo al campo habría que sincronizarlo a mano en cada tecla, y basta un
     `setError` que no lo limpie para que la tarjeta quede mostrando al creador
     de un link anterior. */
  const creador: DatosCreador | null = datosCreadorDeVideo(linkVideo);

  /* "Añadir canal completo": agrega el CANAL por el mismo camino que todo lo
     demás (`guardarCanal` → dedup → persistencia por marca), así que sumar el
     canal desde acá y pegarlo a mano en otro momento no lo duplica. */
  const anadirCanalDelCreador = (urlCanal: string) => {
    const leida = interpretarUrlFuente(urlCanal);
    if (!leida.ok) {
      setError(MENSAJE_RECHAZO[leida.motivo]);
      return;
    }
    guardarCanal(leida.url, leida.nombre);
  };

  const alEnviarVideo = (evento: FormEvent) => {
    evento.preventDefault();
    const leida = interpretarLinkVideo(linkVideo);
    if (!leida.ok) {
      setError(MENSAJE_LINK_VIDEO[leida.motivo]);
      return;
    }
    guardarCanal(leida.url, leida.nombre);
  };

  const alEnviarCreador = (evento: FormEvent) => {
    evento.preventDefault();
    const leida = interpretarCanalCreador(plataformaCreador, valorCreador);
    if (!leida.ok) {
      setError(mensajeCanalCreador(plataformaCreador, leida.motivo));
      return;
    }
    guardarCanal(leida.url, leida.nombre);
  };

  const alEnviarWeb = (evento: FormEvent) => {
    evento.preventDefault();
    const leida = interpretarPaginaWeb(linkWeb);
    if (!leida.ok) {
      setError(MENSAJE_PAGINA_WEB[leida.motivo]);
      return;
    }
    guardarMateriales([materialWeb(leida.url, leida.sitio)], "Esa página ya está en la lista.");
  };

  const alEnviarTexto = (evento: FormEvent) => {
    evento.preventDefault();
    const leido = validarTextoPegado(texto);
    if (!leido.ok) {
      setError(MENSAJE_TEXTO_PEGADO[leido.motivo]);
      return;
    }
    guardarMateriales(
      [materialTexto(leido.texto, titularTexto(leido.texto, tituloTexto))],
      "Ese mismo texto ya está en la lista.",
    );
  };

  /**
   * "Subir archivos" NO tiene sub-pantalla: abre derecho el buscador de
   * archivos del sistema. Lo que sí hay, después de elegir, es una pantalla de
   * CONFIRMACIÓN: nada se anota hasta que el usuario lee que el archivo no se
   * sube. Anotar en el momento de elegir sería registrar algo sin haberle
   * dicho todavía qué se registra.
   */
  /* El procesamiento va aparte del evento del input porque tiene DOS entradas:
     el buscador de archivos y la zona de soltar. Las dos terminan en la misma
     pantalla de confirmación, que es donde se dice que el archivo no se sube. */
  const procesarArchivos = (elegidos: File[]) => {
    if (elegidos.length === 0) return;
    const anotados: MaterialArchivo[] = [];
    const rechazados: string[] = [];
    for (const archivo of elegidos) {
      const leido = anotarArchivo({
        nombre: archivo.name,
        bytes: archivo.size,
        tipoMime: archivo.type,
      });
      if (leido.ok) anotados.push(leido.material);
      else rechazados.push(archivo.name || "un archivo sin nombre");
    }
    setArchivosElegidos(anotados);
    setArchivosRechazados(rechazados);
    irA("archivos");
  };

  const alElegirArchivos = (evento: ChangeEvent<HTMLInputElement>) => {
    const elegidos = Array.from(evento.target.files ?? []);
    // Se limpia el input para que elegir DOS VECES el mismo archivo vuelva a
    // disparar `change` (si no, el navegador considera que no cambió nada).
    evento.target.value = "";
    procesarArchivos(elegidos);
  };

  /* ── Contenido de cada pantalla ── */

  const idError = `${idBase}-error`;
  const describir = error ? idError : undefined;
  const claseCampo = (roto: boolean) =>
    `${CLASE_CAMPO} ${roto ? "border-[#C33B36]" : "border-[#02062B]/[0.12]"}`;

  const pantallaInicio = (
    <>
      <p className="text-[13px] leading-relaxed text-[#02062B]/[0.6]">
        Elegí de dónde sale lo que querés que Atlas aprenda de tu marca. Nada de esto se procesa
        todavía: queda anotado en este navegador hasta que exista la lectura.
      </p>
      {/* La zona de soltar, arriba de todo. Es la MISMA entrada que el atajo
          "Subir archivos" —las dos llaman a `procesarArchivos` y caen en la
          pantalla de confirmación— pero con el gesto que la gente ya tiene en
          la mano. El atajo se queda igual: soltar no sirve con el teclado, y
          en un trackpad arrastrar es incómodo. */}
      <ZonaSoltar onArchivos={procesarArchivos} />

      {/* Dos columnas por dos filas, por pedido de Matías. Con los cuatro
          atajos que quedaron entra justo; "Vincular canal de creador" se fue y
          su función vive ahora en la tarjeta del creador que aparece al pegar
          el link de un video. */}
      {/* `auto-rows-fr`: sin esto cada FILA se dimensiona por su contenido y la de
          arriba queda más baja que la de abajo. El `flex` del `<li>` y el
          `h-full` del botón son la otra mitad: la celda se estira, pero el
          botón de adentro no la llenaba y quedaba flotando. */}
      <ul className="mt-3 grid auto-rows-fr grid-cols-2 gap-2">
        <Atajo
          icono={<Upload className="h-4 w-4" />}
          titulo="Subir archivos"
          detalle="Documentos, PDFs, lo que tengas de la marca."
          onClick={() => inputArchivosRef.current?.click()}
        />
        <Atajo
          logos={["youtube", "instagram", "tiktok"]}
          titulo="Video"
          detalle="Pegá el link de YouTube, Instagram o TikTok."
          onClick={() => irA("video")}
        />
        <Atajo
          icono={<Globe className="h-4 w-4" />}
          titulo="Página web"
          detalle="Un artículo, una nota, la web de un competidor."
          onClick={() => irA("web")}
        />
        <Atajo
          icono={<ClipboardType className="h-4 w-4" />}
          titulo="Texto copiado"
          detalle="Un brief, un guion, tu tono de marca."
          onClick={() => irA("texto")}
        />
      </ul>
    </>
  );

  const pantallaVideo = (
    <form onSubmit={alEnviarVideo} noValidate>
      <NotaDestino>
        El video queda anotado en la lista de canales de referencia, en este navegador. Todavía no se
        descarga, ni se transcribe, ni lo lee Atlas.
      </NotaDestino>
      <label htmlFor={`${idBase}-video`} className="mt-4 block text-[13px] font-medium text-brand-black">
        Link del video
      </label>
      <input
        id={`${idBase}-video`}
        type="text"
        value={linkVideo}
        onChange={(evento) => {
          setLinkVideo(evento.target.value);
          setError(null);
        }}
        placeholder="https://www.youtube.com/watch?v=…"
        autoComplete="off"
        spellCheck={false}
        aria-invalid={error !== null}
        aria-describedby={describir}
        className={claseCampo(error !== null)}
      />
      <p className="mt-1.5 text-[12px] leading-relaxed text-[#02062B]/[0.5]">
        Sirven los de YouTube (también los cortos <code>youtu.be</code> y los Shorts), los reels y
        posteos de Instagram y los videos de TikTok.
      </p>
      {error && <MensajeError id={idError}>{error}</MensajeError>}
      {creador && <TarjetaCreador datos={creador} onAnadirCanal={anadirCanalDelCreador} />}
      <div className="mt-5 flex justify-end">
        <button type="submit" className={CLASE_BOTON_PRIMARIO}>
          Agregar video
        </button>
      </div>
    </form>
  );

  const campoCreador = CAMPO_CREADOR[plataformaCreador];
  const pantallaCreador = (
    <form onSubmit={alEnviarCreador} noValidate>
      <NotaDestino>
        El canal queda anotado en la lista de canales de referencia, en este navegador. No pedimos
        acceso a la cuenta ni traemos sus videos.
      </NotaDestino>

      <fieldset className="mt-4">
        <legend className="text-[13px] font-medium text-brand-black">Plataforma</legend>
        {/* Radios NATIVOS (ocultos con `sr-only`, no con `hidden`): traen
            gratis la navegación con flechas dentro del grupo, el anuncio "1 de
            3" del lector de pantalla y la selección con Espacio. Tres
            `<button aria-pressed>` se veían igual y no hacían nada de eso. */}
        <div className="mt-2 flex flex-wrap gap-2">
          {PLATAFORMAS_CREADOR.map(({ plataforma, nombre }) => (
            <label key={plataforma} className="cursor-pointer">
              <input
                type="radio"
                name={`${idBase}-plataforma`}
                value={plataforma}
                checked={plataformaCreador === plataforma}
                onChange={() => {
                  setPlataformaCreador(plataforma);
                  // El campo pide OTRA COSA: un link de YouTube no es un
                  // usuario de TikTok. Dejarlo escrito garantizaba un error.
                  setValorCreador("");
                  setError(null);
                }}
                className="peer sr-only"
              />
              <span
                className={`inline-flex items-center gap-2 rounded-full border py-2 px-4 text-[13px] font-medium transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#1644EB] ${
                  plataformaCreador === plataforma
                    ? "border-[#1644EB] bg-[#1644EB] text-white"
                    : "border-[#02062B]/[0.12] bg-white text-brand-black hover:bg-[#F0F2F7]"
                }`}
              >
                <MarcoLogo marca={plataforma} tamano="sm" />
                {nombre}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <label
        htmlFor={`${idBase}-creador`}
        className="mt-4 block text-[13px] font-medium text-brand-black"
      >
        {campoCreador.etiqueta}
      </label>
      {/* La `key` fuerza un input NUEVO al cambiar de plataforma: sin ella
          React reusa el mismo nodo del DOM y le arrastra al campo nuevo el
          historial de autocompletado del anterior, que pedía otra cosa. */}
      <input
        key={`campo-${plataformaCreador}`}
        id={`${idBase}-creador`}
        type="text"
        value={valorCreador}
        onChange={(evento) => {
          setValorCreador(evento.target.value);
          setError(null);
        }}
        placeholder={campoCreador.placeholder}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={error !== null}
        aria-describedby={describir}
        className={claseCampo(error !== null)}
      />
      <p className="mt-1.5 text-[12px] leading-relaxed text-[#02062B]/[0.5]">
        {campoCreador.ayuda}
      </p>
      {error && <MensajeError id={idError}>{error}</MensajeError>}
      <div className="mt-5 flex justify-end">
        <button type="submit" className={CLASE_BOTON_PRIMARIO}>
          Vincular canal
        </button>
      </div>
    </form>
  );

  const pantallaWeb = (
    <form onSubmit={alEnviarWeb} noValidate>
      <NotaDestino>
        Guardamos el <strong className="font-semibold">link</strong>, no la página: no la abrimos ni
        copiamos su contenido. Queda en este navegador.
      </NotaDestino>
      <label htmlFor={`${idBase}-web`} className="mt-4 block text-[13px] font-medium text-brand-black">
        Link de la página
      </label>
      <input
        id={`${idBase}-web`}
        type="text"
        value={linkWeb}
        onChange={(evento) => {
          setLinkWeb(evento.target.value);
          setError(null);
        }}
        placeholder="https://ejemplo.com/nota"
        autoComplete="off"
        spellCheck={false}
        aria-invalid={error !== null}
        aria-describedby={describir}
        className={claseCampo(error !== null)}
      />
      <p className="mt-1.5 text-[12px] leading-relaxed text-[#02062B]/[0.5]">
        Cualquier página pública: un artículo, una nota, la web de un competidor.
      </p>
      {error && <MensajeError id={idError}>{error}</MensajeError>}
      <div className="mt-5 flex justify-end">
        <button type="submit" className={CLASE_BOTON_PRIMARIO}>
          Guardar link
        </button>
      </div>
    </form>
  );

  const largoTexto = contarTexto(texto);
  const pantallaTexto = (
    <form onSubmit={alEnviarTexto} noValidate>
      <NotaDestino>
        El texto se guarda <strong className="font-semibold">entero en este navegador</strong>, en
        esta computadora. No viaja a Atlas ni a ningún servidor, y Atlas todavía no lo lee.
      </NotaDestino>
      <label
        htmlFor={`${idBase}-titulo`}
        className="mt-4 block text-[13px] font-medium text-brand-black"
      >
        Título <span className="font-normal text-[#02062B]/[0.45]">(opcional)</span>
      </label>
      <input
        id={`${idBase}-titulo`}
        type="text"
        value={tituloTexto}
        onChange={(evento) => setTituloTexto(evento.target.value)}
        placeholder="Tono de marca"
        autoComplete="off"
        className={claseCampo(false)}
      />
      <label
        htmlFor={`${idBase}-texto`}
        className="mt-4 block text-[13px] font-medium text-brand-black"
      >
        Texto
      </label>
      <textarea
        id={`${idBase}-texto`}
        value={texto}
        onChange={(evento) => {
          setTexto(evento.target.value);
          setError(null);
        }}
        rows={7}
        placeholder="Pegá acá lo que copiaste…"
        aria-invalid={error !== null}
        aria-describedby={describir}
        className={`${claseCampo(error !== null)} resize-y leading-relaxed`}
      />
      <p className="mt-1.5 text-[12px] text-[#02062B]/[0.5]">
        {/* El contador cuenta lo MISMO que valida (`contarTexto`): con los
            saltos de línea de Windows, el largo del textarea y el del texto
            guardado no coinciden. */}
        {largoTexto.toLocaleString("es-AR")} de {MAX_TEXTO.toLocaleString("es-AR")} caracteres
      </p>
      {error && <MensajeError id={idError}>{error}</MensajeError>}
      <div className="mt-5 flex justify-end">
        <button type="submit" className={CLASE_BOTON_PRIMARIO}>
          Guardar texto
        </button>
      </div>
    </form>
  );

  const pantallaArchivos = (
    <div>
      {/* LA PANTALLA QUE NO PUEDE MENTIR. Se lee ANTES de anotar nada: el
          usuario acaba de elegir archivos en el buscador de su sistema, que es
          exactamente el gesto de "subir", y lo que pasa es otra cosa. */}
      <NotaDestino alerta>
        <strong className="font-semibold">Los archivos no se suben a ningún lado.</strong> No hay
        dónde guardarlos todavía: lo que anotamos es el nombre, el tamaño y el tipo, en este
        navegador. El archivo se queda en tu computadora y, cuando exista la lectura, vas a tener que
        volver a elegirlo.
      </NotaDestino>

      {/* Mismo rótulo que las sub-listas de la pantalla de atrás: lo que se
          está por anotar y lo ya anotado son la misma cosa en dos momentos. */}
      <p className={`mt-4 ${CLASE_ROTULO}`}>Vamos a anotar ({archivosElegidos.length})</p>
      <ul className="mt-2 space-y-1.5">
        {archivosElegidos.map((archivo) => (
          <li
            key={archivo.id}
            className="flex items-center gap-3 rounded-xl border border-[#02062B]/[0.08] bg-white py-2.5 px-4"
          >
            <FileText className="h-4 w-4 shrink-0 text-[#02062B]/[0.4]" aria-hidden="true" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium text-brand-black">
                {archivo.nombre}
              </span>
              <span className="block text-[12px] text-[#02062B]/[0.5]">
                {archivo.formato} · {formatearTamano(archivo.bytes)} · no se sube
              </span>
            </span>
          </li>
        ))}
      </ul>

      {archivosRechazados.length > 0 && (
        <p className="mt-3 text-[12.5px] leading-relaxed text-[#C33B36]">
          No pudimos anotar {archivosRechazados.length === 1 ? "este" : "estos"}:{" "}
          {archivosRechazados.join(", ")}. Están vacíos (0 bytes) o el navegador no nos dio su
          nombre.
        </p>
      )}
      {error && <MensajeError id={idError}>{error}</MensajeError>}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          className={CLASE_BOTON_SECUNDARIO}
          onClick={() => inputArchivosRef.current?.click()}
        >
          Elegir otros
        </button>
        <button
          type="button"
          className={CLASE_BOTON_PRIMARIO}
          disabled={archivosElegidos.length === 0}
          onClick={() =>
            guardarMateriales(archivosElegidos, "Esos archivos ya estaban anotados en la lista.")
          }
        >
          Anotar {archivosElegidos.length === 1 ? "el nombre" : "los nombres"}
        </button>
      </div>
    </div>
  );

  /** La pregunta de descarte. Reemplaza el cuerpo en vez de abrir un segundo
   *  diálogo encima, por lo mismo que las sub-pantallas: dos `aria-modal`
   *  apilados se declaran los dos como "lo único que existe". */
  const pantallaConfirmarCierre = (
    <div>
      <NotaDestino alerta>
        <strong className="font-semibold">Se pierde lo que escribiste.</strong> Todavía no guardamos
        nada de esta pantalla: si cerrás ahora, el texto, los links y los archivos elegidos se
        descartan y no hay forma de recuperarlos.
      </NotaDestino>
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          ref={seguirEditandoRef}
          className={CLASE_BOTON_SECUNDARIO}
          onClick={() => setConfirmandoCierre(false)}
        >
          Seguir editando
        </button>
        <button type="button" className={CLASE_BOTON_DESTRUCTIVO} onClick={onCerrar}>
          Descartar
        </button>
      </div>
    </div>
  );

  const CONTENIDO: Record<Pantalla, ReactNode> = {
    inicio: pantallaInicio,
    video: pantallaVideo,
    creador: pantallaCreador,
    web: pantallaWeb,
    texto: pantallaTexto,
    archivos: pantallaArchivos,
  };

  const enInicio = pantalla === "inicio";
  const idTitulo = `${idBase}-titulo-dialogo`;

  return createPortal(
    <div
      // `z-[120]`: por encima del resto de los modales de la app (el de
      // Tiendanube va en 90, el sheet de auth en 100). Clase estática, no
      // armada en runtime: Tailwind no compila las que no ve escritas.
      // La opacidad va en la forma `/[0.5]` (la misma que usa toda la sección)
      // y sobre un hex arbitrario: `bg-brand-black/50` NO compila, porque el
      // color es una variable CSS y Tailwind no le puede calcular el alfa.
      className="fixed inset-0 z-[120] flex items-end justify-center bg-[#02062B]/[0.5] backdrop-blur-[2px] sm:items-center sm:p-6"
      // `mouseDown` y no `click`: con `click`, empezar a seleccionar texto
      // adentro del panel y soltar afuera cierra el modal y se pierde lo
      // escrito. Y con algo escrito no cierra derecho: pregunta (`pedirCierre`),
      // que es el mismo desenlace que este `mouseDown` vino a evitar.
      onMouseDown={(evento) => {
        if (evento.target === evento.currentTarget) pedirCierre();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        // 760 y no 560: los atajos pasaron a dos columnas y a 560 cada tarjeta
        // quedaba en ~250px, con el detalle partido en tres renglones. Ancho por
        // pedido de Matías.
        className="flex max-h-[92vh] w-full max-w-[760px] flex-col overflow-hidden rounded-t-2xl bg-white shadow-[0_24px_60px_rgba(2,6,43,0.28)] sm:rounded-2xl"
      >
        <header className="flex shrink-0 items-center gap-2 border-b border-[#02062B]/[0.08] py-4 px-5">
          {/* La vuelta atrás es un `<button>` de verdad, primero en el orden de
              tabulación y con rótulo propio: se alcanza con Tab y se dispara
              con Enter y con Espacio. */}
          {!enInicio && !confirmandoCierre && (
            <button
              type="button"
              onClick={() => irA("inicio")}
              aria-label="Volver a Añadir fuente"
              className={CLASE_ICONO_HEADER}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <h2
            id={idTitulo}
            ref={tituloRef}
            // `tabIndex={-1}`: enfocable por código (para anunciar el cambio de
            // pantalla) pero fuera de la rueda de tabulación.
            tabIndex={-1}
            className="min-w-0 flex-1 truncate text-[15px] font-semibold text-brand-black outline-none"
          >
            {confirmandoCierre ? "¿Descartar lo que escribiste?" : TITULO[pantalla]}
          </h2>
          {/* Con la pregunta abierta, la ✕ la cancela: es el gesto de "sacame
              esto de encima", y ahí lo de encima es la pregunta. Descartar
              tiene su propio botón, rojo y con nombre. */}
          <button
            type="button"
            onClick={() => (confirmandoCierre ? setConfirmandoCierre(false) : pedirCierre())}
            aria-label={confirmandoCierre ? "Seguir editando" : "Cerrar"}
            className={CLASE_ICONO_HEADER}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto py-5 px-5">
          {confirmandoCierre ? pantallaConfirmarCierre : CONTENIDO[pantalla]}
        </div>

        {/* Vive FUERA de la pantalla de archivos y siempre montado: lo dispara
            el atajo de la pantalla inicial (que no tiene sub-pantalla) y
            también el "Elegir otros" de la confirmación. */}
        <input
          ref={inputArchivosRef}
          type="file"
          multiple
          className="hidden"
          onChange={alElegirArchivos}
        />
      </div>
    </div>,
    document.body,
  );
};

export default ModalAnadirFuente;
